import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";

// Star Rating Component
const StarRating = ({ value, onChange, maxStars = 5 }) => {
  const [hoverValue, setHoverValue] = useState(0);

  return (
    <div className="flex gap-1">
      {[...Array(maxStars)].map((_, index) => {
        const starValue = index + 1;
        return (
          <button
            key={index}
            type="button"
            onClick={() => onChange(starValue)}
            onMouseEnter={() => setHoverValue(starValue)}
            onMouseLeave={() => setHoverValue(0)}
            className="p-1 hover:scale-110 transition-transform"
          >
            <Star
              className={`w-8 h-8 transition-colors ${
                starValue <= (hoverValue || value)
                  ? 'fill-[#B8860B] text-[#B8860B]'
                  : 'text-slate-300'
              }`}
            />
          </button>
        );
      })}
      {value > 0 && (
        <span className="ml-2 text-sm text-muted-foreground self-center">
          {value} / {maxStars}
        </span>
      )}
    </div>
  );
};

// Skill Rating Question Component
const SkillRatingQuestion = ({ id, skill, description, value, onChange }) => {
  return (
    <div className="p-4 border rounded-lg mb-4 hover:bg-slate-50 transition-colors" data-testid={`skill-${id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium text-slate-800">{skill}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <StarRating value={value || 0} onChange={onChange} />
      </div>
    </div>
  );
};

// Overall Grade Question Component
const OverallGradeQuestion = ({ id, question, value, onChange }) => {
  const grades = [
    { value: "A", label: "Exceptional", description: "Consistently exceeds expectations, expert level" },
    { value: "B", label: "Strong", description: "Frequently exceeds expectations, highly proficient" },
    { value: "C", label: "Competent", description: "Meets expectations, solid performer" },
    { value: "D", label: "Developing", description: "Sometimes meets expectations, growing skills" },
    { value: "E", label: "Learning", description: "Still building foundational skills" }
  ];

  return (
    <div className="question-card bg-white rounded-lg border p-6 mb-6" data-testid={`question-${id}`}>
      <p className="text-base font-medium text-slate-800 mb-4">{question}</p>
      <div className="grid gap-3">
        {grades.map((grade) => (
          <div
            key={grade.value}
            onClick={() => onChange(grade.value)}
            className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
              value === grade.value
                ? 'border-[#003366] bg-blue-50'
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${
              value === grade.value ? 'bg-[#003366] text-white' : 'bg-slate-200 text-slate-600'
            }`}>
              {grade.value}
            </div>
            <div className="flex-1">
              <p className="font-semibold text-slate-800">{grade.label}</p>
              <p className="text-sm text-muted-foreground">{grade.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Text Question Component
const TextQuestion = ({ id, question, value, onChange, placeholder }) => {
  const [localValue, setLocalValue] = useState(value || "");

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleBlur = () => {
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  return (
    <div className="question-card bg-white rounded-lg border p-6 mb-6" data-testid={`question-${id}`}>
      <label className="block text-base font-medium text-slate-800 mb-3">{question}</label>
      <Textarea
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder || "Enter your response..."}
        rows={4}
        className="w-full"
        data-testid={`input-${id}`}
      />
    </div>
  );
};

export const SelfGradingSection = ({ responses, onSave, profile }) => {
  const getValue = (questionId) => responses?.[questionId];

  const technicalSkills = [
    { id: "programming", skill: "Programming & Development", description: "Coding, scripting, software development" },
    { id: "architecture", skill: "System Architecture", description: "Designing scalable systems and solutions" },
    { id: "cloud", skill: "Cloud Technologies", description: "AWS, Azure, GCP, cloud infrastructure" },
    { id: "data", skill: "Data & Analytics", description: "Databases, data pipelines, analytics" },
    { id: "security", skill: "Security", description: "Cybersecurity, compliance, risk management" },
    { id: "devops", skill: "DevOps & Automation", description: "CI/CD, infrastructure as code, automation" },
    { id: "ai_ml", skill: "AI & Machine Learning", description: "AI tools, ML models, emerging tech" },
    { id: "testing", skill: "Testing & QA", description: "Quality assurance, test automation" }
  ];

  const softSkills = [
    { id: "communication", skill: "Communication", description: "Written and verbal communication skills" },
    { id: "teamwork", skill: "Teamwork & Collaboration", description: "Working effectively with others" },
    { id: "leadership", skill: "Leadership", description: "Guiding and mentoring others" },
    { id: "problem_solving", skill: "Problem Solving", description: "Analytical thinking and solutions" },
    { id: "adaptability", skill: "Adaptability", description: "Flexibility and willingness to learn" },
    { id: "time_management", skill: "Time Management", description: "Prioritization and meeting deadlines" }
  ];

  return (
    <div className="space-y-2">
      {/* Section intro */}
      <Card className="bg-[#f0f7ff] border-[#003366]/20 mb-8">
        <CardContent className="pt-6">
          <p className="text-sm text-[#003366]">
            This section asks you to honestly assess your own capabilities. Your self-assessment helps us understand 
            your perception of your strengths and areas for growth. Please rate yourself on a scale of 1-5 stars, 
            where 5 represents expert-level proficiency.
          </p>
        </CardContent>
      </Card>

      {/* 4.1 Technical Skills Self-Assessment */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          4.1 Technical Skills Self-Assessment
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Rate your proficiency in each technical area (1 = Beginner, 5 = Expert)
        </p>

        {technicalSkills.map((skill) => (
          <SkillRatingQuestion
            key={skill.id}
            id={`tech_${skill.id}`}
            skill={skill.skill}
            description={skill.description}
            value={getValue(`tech_${skill.id}`)}
            onChange={(val) => onSave(`tech_${skill.id}`, val)}
          />
        ))}
      </div>

      {/* 4.2 Soft Skills Self-Assessment */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          4.2 Soft Skills Self-Assessment
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Rate your proficiency in each soft skill area (1 = Developing, 5 = Exceptional)
        </p>

        {softSkills.map((skill) => (
          <SkillRatingQuestion
            key={skill.id}
            id={`soft_${skill.id}`}
            skill={skill.skill}
            description={skill.description}
            value={getValue(`soft_${skill.id}`)}
            onChange={(val) => onSave(`soft_${skill.id}`, val)}
          />
        ))}
      </div>

      {/* 4.3 Overall Self-Grade */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          4.3 Overall Self-Grade
        </h3>

        <OverallGradeQuestion
          id="overall_grade"
          question="Based on your current role requirements, how would you grade your overall performance?"
          value={getValue("overall_grade")}
          onChange={(val) => onSave("overall_grade", val)}
        />

        <OverallGradeQuestion
          id="technical_grade"
          question="How would you grade your overall technical capabilities?"
          value={getValue("technical_grade")}
          onChange={(val) => onSave("technical_grade", val)}
        />

        <OverallGradeQuestion
          id="growth_potential"
          question="How would you rate your growth potential and ability to take on new challenges?"
          value={getValue("growth_potential")}
          onChange={(val) => onSave("growth_potential", val)}
        />
      </div>

      {/* 4.4 Reflection */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          4.4 Reflection
        </h3>

        <TextQuestion
          id="strengths_reflection"
          question="What do you consider your top 3 strengths that add the most value to your team?"
          value={getValue("strengths_reflection")}
          onChange={(val) => onSave("strengths_reflection", val)}
          placeholder="Describe your key strengths..."
        />

        <TextQuestion
          id="growth_areas"
          question="What are the top 3 areas where you'd like to improve or develop further?"
          value={getValue("growth_areas")}
          onChange={(val) => onSave("growth_areas", val)}
          placeholder="Describe areas for growth..."
        />

        <TextQuestion
          id="support_needed"
          question="What support or resources would help you reach your full potential?"
          value={getValue("support_needed")}
          onChange={(val) => onSave("support_needed", val)}
          placeholder="Training, mentorship, tools, etc..."
        />
      </div>
    </div>
  );
};

export default SelfGradingSection;
